import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import { PrintOutlined, EventBusy } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { timetableService, TeacherPeriod, TimetableGrid } from '../../services/timetableService';
import EmptyState from '../../components/common/EmptyState';
import { ToneChip } from '../../components/common/StatusChip';
import WeekGrid, { GridItem } from '../../components/timetable/WeekGrid';
import LessonCard from '../../components/timetable/LessonCard';
import BusyIcon from '../../components/common/BusyIcon';
import { useTimetablePdf } from '../../components/timetable/useTimetablePdf';

/** The signed-in teacher's week from the live timetable. */
const MyTimetablePage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState<{ grid: TimetableGrid | null; periods: TeacherPeriod[] } | null>(null);
  const { download, busy: printing } = useTimetablePdf();

  useEffect(() => {
    let cancelled = false;
    timetableService.getMine()
      .then((d) => { if (!cancelled) setData(d); })
      .catch((err: any) => { if (!cancelled) { setData({ grid: null, periods: [] }); enqueueSnackbar(err?.message || 'Could not load your timetable', { variant: 'error' }); } });
    return () => { cancelled = true; };
  }, [enqueueSnackbar]);

  const items: GridItem[] = useMemo(() => (data?.periods ?? []).map((p, i) => ({
    id: `${p.day}:${p.period}:${i}`,
    day: p.day,
    period: p.period,
    span: p.span,
    node: <LessonCard subjectId={p.subjectId} title={p.subjectName} subtitle={p.classLabel} meta={p.roomName} />,
  })), [data]);

  const weekly = (data?.periods ?? []).reduce((n, p) => n + p.span, 0);
  const classes = new Set((data?.periods ?? []).map((p) => p.classLabel)).size;
  const today = new Date().getDay();

  const print = () => download(() => timetableService.getMyPrint(), 'My_Timetable');

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }} sx={{ mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" fontWeight={700}>My timetable</Typography>
          <Typography variant="body2" color="text.secondary">Your week from the published timetable.</Typography>
        </Box>
        {!!data?.periods.length && (
          <Button variant="outlined" color="inherit" startIcon={<BusyIcon busy={printing}><PrintOutlined /></BusyIcon>} onClick={print} disabled={printing} sx={{ textTransform: 'none', borderColor: 'divider' }}>
            {printing ? 'Preparing…' : 'Print'}
          </Button>
        )}
      </Stack>

      {!data ? (
        <Skeleton variant="rounded" height={380} sx={{ borderRadius: '12px' }} />
      ) : !data.grid ? (
        <EmptyState icon={<EventBusy />} title="No timetable yet" description="Your week appears here once the school publishes its timetable." />
      ) : !data.periods.length ? (
        <EmptyState icon={<EventBusy />} title="No periods this week" description="You aren't teaching any classes in the published timetable." />
      ) : (
        <>
          <Stack direction="row" spacing={0.75} sx={{ mb: 2 }}>
            <ToneChip tone="highlight" label={`${weekly} periods a week`} />
            <ToneChip tone="neutral" label={`${classes} class${classes === 1 ? '' : 'es'}`} />
          </Stack>
          <WeekGrid grid={data.grid} items={items} today={today} />
        </>
      )}
    </Box>
  );
};

export default MyTimetablePage;
